import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { transliterate } from 'transliteration'
import ArticleFilters from '../components/ArticleFilters'
import SortControls from '../components/SortControls'
import Search from '../components/Search'
import { normalizeString } from '../utils/helpers'
import { translations } from '../data/translations'
import { extractFirstImageSrc, sanitizeHtml } from '../utils/html'
import { safeReadJson, safeWriteJson } from '../utils/storage'
import { getArticles, onArticlesUpdated } from '../utils/articlesRepository'
import defaultPreviewImage from '../img/articles/article-preview.jpg'

const ShareIcon = () => (
  <svg
    className="share-button-icon"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
    <path d="M12 16V4" />
    <path d="m7 9 5-5 5 5" />
  </svg>
)

const CheckIcon = () => (
  <svg
    className="share-button-icon"
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
  >
    <path d="m20 6-11 11-5-5" />
  </svg>
)

const slugify = (name) =>
  transliterate(name)
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()

const parseDate = (dateString) => {
  if (!dateString) return null
  const [day, month, year] = dateString.split('.').map(Number)
  if (!day || !month || !year) return null
  return new Date(year, month - 1, day)
}

const ArticlesView = ({ language, setIsArticleOpen, setArticleMeta }) => {
  const [articles, setArticles] = useState(() => getArticles())
  const [selectedTypes, setSelectedTypes] = useState(() =>
    safeReadJson('selectedTypes_articles', [])
  )
  const [sortBy, setSortBy] = useState('date')
  const [sortOrder, setSortOrder] = useState('desc')
  const [expandedArticle, setExpandedArticle] = useState(null)
  const [filtersOpen, setFiltersOpen] = useState(() =>
    safeReadJson('filtersOpen_articles', false)
  )
  const [zoomedImage, setZoomedImage] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showArticleScrollTop, setShowArticleScrollTop] = useState(false)
  const [copiedArticle, setCopiedArticle] = useState(null)
  const articleModalContentRef = useRef(null)

  const t = translations[language]

  useEffect(() => {
    return onArticlesUpdated(setArticles)
  }, [])

  const buildMeta = useCallback(
    (article) => {
      const previewImage =
        extractFirstImageSrc(article.articleContent) || defaultPreviewImage
      const slug = slugify(article.articleName)

      return {
        title: article.articleName,
        description: article.articleDate
          ? `${article.articleDate} | ${translations[language].metaDescription.articles}`
          : translations[language].metaDescription.articles,
        image: previewImage,
        url: `${window.location.origin}/?article=${encodeURIComponent(slug)}`,
      }
    },
    [language]
  )

  useEffect(() => {
    safeWriteJson('selectedTypes_articles', selectedTypes)
  }, [selectedTypes])

  useEffect(() => {
    safeWriteJson('filtersOpen_articles', filtersOpen)
  }, [filtersOpen])

  useEffect(() => {
    setSortOrder(sortBy === 'date' ? 'desc' : 'asc')
  }, [sortBy])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const articleSlug = urlParams.get('article')

    if (!articleSlug) {
      setExpandedArticle(null)
      setArticleMeta(null)
      return
    }

    const article = articles.find(
      (item) => slugify(item.articleName) === articleSlug
    )
    if (!article) {
      setExpandedArticle(null)
      setArticleMeta(null)
      return
    }

    setExpandedArticle(article)
    setArticleMeta(buildMeta(article))
  }, [articles, buildMeta, setArticleMeta])

  useEffect(() => {
    if (expandedArticle) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [expandedArticle])

  useEffect(() => {
    setIsArticleOpen(!!expandedArticle)
  }, [expandedArticle, setIsArticleOpen])

  useEffect(() => {
    const modalContent = articleModalContentRef.current
    if (!modalContent) return

    const handleScroll = () => {
      setShowArticleScrollTop(modalContent.scrollTop > 200)
    }

    modalContent.addEventListener('scroll', handleScroll)
    handleScroll()

    return () => modalContent.removeEventListener('scroll', handleScroll)
  }, [expandedArticle])

  const filteredArticles = useMemo(() => {
    return articles
      .filter((article) => {
        return selectedTypes.length
          ? article.articleType.some((type) => selectedTypes.includes(type))
          : true
      })
      .filter((article) => {
        if (!searchTerm) return true

        const normalizedSearch = normalizeString(searchTerm)
        const fieldsToSearch = [normalizeString(article.articleName)]

        if (expandedArticle?.articleName === article.articleName) {
          fieldsToSearch.push(normalizeString(article.articleContent))
        }

        return fieldsToSearch.some((field) => field.includes(normalizedSearch))
      })
      .sort((a, b) => {
        const modifier = sortOrder === 'asc' ? 1 : -1

        if (sortBy === 'date') {
          const dateA = parseDate(a.articleDate)
          const dateB = parseDate(b.articleDate)

          if (!dateA && !dateB) {
            return a.articleName.localeCompare(b.articleName) * modifier
          }

          if (!dateA) return 1
          if (!dateB) return -1
          return (dateA - dateB) * modifier
        }

        return a.articleName.localeCompare(b.articleName) * modifier
      })
  }, [articles, expandedArticle, searchTerm, selectedTypes, sortBy, sortOrder])

  const copyArticleLink = (articleName) => {
    const articleLink = `${window.location.origin}/?article=${encodeURIComponent(
      slugify(articleName)
    )}`

    navigator.clipboard
      .writeText(articleLink)
      .then(() => {
        setCopiedArticle(articleName)
        setTimeout(() => setCopiedArticle(null), 2000)
      })
      .catch(() => {})
  }

  const openArticle = (article, event) => {
    if (event.target.closest('.share-button')) {
      event.stopPropagation()
      return
    }

    if (event.target.tagName === 'IMG' && window.innerWidth < 768) {
      event.stopPropagation()
    }

    setExpandedArticle(article)
    setArticleMeta(buildMeta(article))
    window.history.pushState(
      {},
      '',
      `?article=${encodeURIComponent(slugify(article.articleName))}`
    )
  }

  const closeArticle = () => {
    setExpandedArticle(null)
    setShowArticleScrollTop(false)
    window.history.pushState({}, '', window.location.pathname)
    setArticleMeta(null)
  }

  const resetFilters = () => {
    setSelectedTypes([])
    setSortBy('date')
    setSortOrder('desc')
    setExpandedArticle(null)
    setSearchTerm('')
    window.history.pushState({}, '', window.location.pathname)
    setArticleMeta(null)
  }

  const scrollToArticleTop = () => {
    if (!articleModalContentRef.current) return
    articleModalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <>
      <ArticleFilters
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        selectedTypes={selectedTypes}
        setSelectedTypes={setSelectedTypes}
        language={language}
      />

      <Search
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        language={language}
      />

      <SortControls
        activeView="articles"
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        resetFilters={resetFilters}
        filteredCount={filteredArticles.length}
        totalCount={articles.length}
        language={language}
      />

      <div className="articles-grid">
        {filteredArticles.map((article) => {
          const previewImage = extractFirstImageSrc(article.articleContent)
          return (
            <div
              key={article.articleName}
              className="article-card"
              onClick={(event) => openArticle(article, event)}
            >
              <img
                src={previewImage || defaultPreviewImage}
                alt={article.articleName}
                className="article-preview-image"
                loading="lazy"
                onError={(event) => {
                  event.target.src = defaultPreviewImage
                }}
              />

              <div className="article-card-content">
                <h3 className="article-title">{article.articleName}</h3>
                <div className="article-meta">
                  {article.articleDate && (
                    <p className="article-date">{article.articleDate}</p>
                  )}
                  <button
                    className="share-button"
                    onClick={() => copyArticleLink(article.articleName)}
                    title={t.share}
                    aria-label={t.share}
                    type="button"
                  >
                    <span>
                      {copiedArticle === article.articleName ? t.copied : t.share}
                    </span>
                    {copiedArticle === article.articleName ? <CheckIcon /> : <ShareIcon />}
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {expandedArticle && (
        <div
          className="article-modal"
          onClick={(event) => {
            if (event.target.classList.contains('article-modal')) {
              closeArticle()
            }
          }}
        >
          <button
            className="article-modal-close"
            onClick={closeArticle}
            aria-label="Close article"
            type="button"
          >
            ?
          </button>

          <div className="article-modal-content" ref={articleModalContentRef}>
            <div className="article-modal-content-inner">
              <h2 className="article-modal-title">{expandedArticle.articleName}</h2>
              {expandedArticle.articleDate && (
                <p className="article-modal-date">{expandedArticle.articleDate}</p>
              )}
              <div
                className="article-modal-body"
                onClick={(event) => {
                  if (event.target.tagName === 'IMG' && window.innerWidth < 768) {
                    setZoomedImage(event.target.src)
                  }
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(expandedArticle.articleContent),
                }}
              />
            </div>
          </div>

          {showArticleScrollTop && (
            <button
              className="scroll-to-top article-scroll-to-top"
              onClick={scrollToArticleTop}
              aria-label="Scroll article to top"
              type="button"
            />
          )}
        </div>
      )}

      {zoomedImage && (
        <div className="zoomed-image-modal" onClick={() => setZoomedImage(null)}>
          <img src={zoomedImage} alt="Zoomed" className="zoomed-image" />
        </div>
      )}
    </>
  )
}

export default ArticlesView
